import type { CSSProperties, ReactNode } from 'react';
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import type { SxProps, Theme } from '@mui/material';
import { Box } from '@mui/material';
import type { Interpolation } from 'styled-components';
import { useModuleWithImage } from './hooks';
import {
  GridContainer,
  GridItem,
} from '../../../../component_library/components/Grid';
import { LinkButton } from '../../../../component_library/components/Button';
import Typography, {
  TypographyVariant,
} from '../../../../component_library/components/Typography';
import { CheckmarkBulletRow } from './checkmark-bullet-row';
import {
  getAvailableColumnsCount,
  GRID_CONFIGS_FOR_BREAKPOINTS,
} from '../../../../component_library/utils/gridHelper';
import { ButtonVariant } from '../../../../component_library/components/Button/button-style-helper';
import type { IPropsWithTestId } from '../../../../constants/data-element-tracking-ids';
import { BREAKPOINT } from '../../../../component_library/utils/breakpoints';
import type { SettingBasedOnBreakpoints } from '../../../../hooks/use-setting-based-on-breakpoints';
import type { IModuleWithImageData } from '../../../../dotcms/dotcms-models/dotcms-module-with-image';
import { ImageHorizontalAlignment } from '../../../../dotcms/dotcms-models/dotcms-module-with-image';
import type { IModelBasedOnEnrollmentStatus } from '../../../../dotcms/dotcms-models/model-based-on-enrollment-status-helper';
import { getDataBasedOnEnrollmentStatus } from '../../../../dotcms/dotcms-models/model-based-on-enrollment-status-helper';
import { resizeDotcmsImage } from '../../../../dotcms/dotcms-image-tool';
import type { Page } from '../../../../app/page-url-path';
import { useGetRouteToIssuerPage } from '../../../../app/get-route-to-page';
import HtmlBox from '../../../../component_library/components/HtmlBox';
import { useFooterContext } from '../../footer/hooks/useFooterContext';
import { useGetPageConditionRequirementResults } from '../../../../app/pages-requiring-condition';
import { useThemeToken } from '../../../../hooks/use-get-theme/use-get-theme';

const MODULE_IMAGE_HEIGHT_IN_PIXELS = 360;

export const MODULE_IMAGE_BASE_STYLING: CSSProperties = {
  maxWidth: '100%',
  objectFit: 'contain',
  borderRadius: '10px',
};

const MODULE_ROW_STYLE: CSSProperties = {
  justifyContent: 'space-between',
};

interface IProps {
  data: IModuleWithImageData;
  headlineTypographyVariant?: SettingBasedOnBreakpoints<TypographyVariant>;
  contentTypographyVariant?: SettingBasedOnBreakpoints<TypographyVariant>;
  button: IPropsWithTestId & {
    pageForButton: IModelBasedOnEnrollmentStatus<Page>;
    buttonVariant?: ButtonVariant;
  };
  renderButtonElement?: (
    props: IPropsWithTestId & {
      label: string | undefined;
      buttonVariant: ButtonVariant;
    },
  ) => ReactNode;
  imageColumnWidth?: {
    desktop: number;
    tablet: number;
    tabletMini: number;
    mobile: number;
  };
  columnsSpacing?: SettingBasedOnBreakpoints<number>;
  sx?: SxProps<Theme>;
}

/**
 * This is a generic implementation of the rows in
 * https://www.figma.com/file/U2kmiap0vtjXTMaNWeTWZx/NEW-espp.com%2Fcompanyabc?node-id=2486%3A81545
 * Each row has an image, a headline, and some text below the headline.
 * The image can either be to the left or to the right of the content.
 * Optionally, below the content, there can be a divider and then some checkmark bullet points.
 */
const ModuleWithImage: React.FC<IProps> = ({
  data,
  headlineTypographyVariant = data.headerMaximized
    ? {
      [BREAKPOINT.DESKTOP]: TypographyVariant.H1,
      [BREAKPOINT.TABLET_MINI]: TypographyVariant.H2,
    }
    : {
      [BREAKPOINT.DESKTOP]: TypographyVariant.H2,
      [BREAKPOINT.TABLET_MINI]: TypographyVariant.H3,
    },
  contentTypographyVariant = {
    [BREAKPOINT.DESKTOP]: TypographyVariant.LARGE_BODY_REGULAR,
    [BREAKPOINT.TABLET_MINI]: TypographyVariant.MEDIUM_BODY_REGULAR,
  },
  button,
  renderButtonElement,
  imageColumnWidth = {
    desktop: data.headerMaximized && !data.headerCentered ? 3 : 5,
    tablet: data.headerMaximized && !data.headerCentered ? 3 : 5,
    tabletMini: 3,
    mobile: getAvailableColumnsCount(
      GRID_CONFIGS_FOR_BREAKPOINTS[BREAKPOINT.MOBILE],
    ),
  },
  columnsSpacing = {
    [BREAKPOINT.DESKTOP]: 5,
    [BREAKPOINT.TABLET_MINI]: 3,
  },
  sx = [],
}) => {
  const {
    theme,
    muiTheme,
    isMobile,
    getSettingBasedOnBreakpoint,
    userStates,
    isEnrollmentOrScheduledEnrollmentOpen,
    isUserTerminated,
  } = useModuleWithImage();
  const themeToken = useThemeToken();
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const imageGridItemRef = useRef<HTMLDivElement | null>(null);
  const contentGridItemRef = useRef<HTMLDivElement | null>(null);

  const resizeImage = useCallback(() => {
    if (
      !data.image ||
      !imageGridItemRef.current ||
      !contentGridItemRef.current
    ) {
      return;
    }

    // Use width based on number of columns for the image
    const imageGridItemStyles = getComputedStyle(imageGridItemRef.current);
    const expectedImageWidth =
      parseInt(imageGridItemStyles.width, 10) -
      parseInt(imageGridItemStyles.paddingLeft, 10) -
      parseInt(imageGridItemStyles.paddingRight, 10);

    // Use height based on text content height or predefined values
    const contentGridItemStyles = getComputedStyle(contentGridItemRef.current);
    let expectedImageHeight =
      parseInt(contentGridItemStyles.height, 10) -
      parseInt(contentGridItemStyles.paddingTop, 10) -
      parseInt(contentGridItemStyles.paddingBottom, 10);
    expectedImageHeight = isMobile
      ? Math.round(expectedImageWidth / 1.2)
      : Math.max(MODULE_IMAGE_HEIGHT_IN_PIXELS, expectedImageHeight);

    const resizedImageUrl = resizeDotcmsImage(data.image, {
      widthInPixels: expectedImageWidth,
      heightInPixels: expectedImageHeight,
    });
    setImageUrl(resizedImageUrl);
  }, [imageGridItemRef, contentGridItemRef, data.image, setImageUrl, isMobile]);

  const { setFootnote } = useFooterContext();
  useEffect(() => {
    if (!data?.footnote) {
      return () => {};
    }

    const { cleanUpFootnoteForUnmount } = setFootnote(data.footnote);
    return cleanUpFootnoteForUnmount;
  }, [data?.footnote, setFootnote]);

  useEffect(() => {
    if (!data.showImage) {
      return () => {};
    }

    let windowResizeTimeoutId: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(windowResizeTimeoutId);
      windowResizeTimeoutId = setTimeout(resizeImage, 300);
    };

    window.addEventListener('resize', handleResize);

    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);
  });

  useEffect(() => {
    if (data.showImage) {
      resizeImage();
    }
  }, [resizeImage, data.showImage]);

  const centeredHeaderStyles: Interpolation<Theme> = {
    textAlign: 'center',
    marginBottom: '88px',
    [muiTheme.breakpoints.down('tablet')]: {
      marginBottom: '64px',
    },
  };

  const headerStyles: Interpolation<Theme> = {
    marginBottom: '20px',
  };

  const NON_IMAGE_CONTENT_STYLE: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    [muiTheme.breakpoints.down('tabletMini')]: {
      marginTop: '40px',
      '&.MuiGrid-item': {
        paddingTop: '0',
      },
    },
  };

  const buttonData = data.button
    ? getDataBasedOnEnrollmentStatus(
      data.button,
      userStates,
      isEnrollmentOrScheduledEnrollmentOpen,
    )
    : undefined;
  const buttonPageByStatus = getDataBasedOnEnrollmentStatus(
    button.pageForButton,
    userStates,
    isEnrollmentOrScheduledEnrollmentOpen,
  );
  const buttonPage = buttonPageByStatus ?? button.pageForButton.defaultData;
  const isPageForButtonAvailable =
    useGetPageConditionRequirementResults()[buttonPage];
  const buttonRoute = useGetRouteToIssuerPage(buttonPage);

  const imageGridItem = data.showImage ? (
    <GridItem
      ref={imageGridItemRef}
      size={{
        desktop: imageColumnWidth.desktop,
        tablet: imageColumnWidth.tablet,
        tabletMini: imageColumnWidth.tabletMini,
        mobile: imageColumnWidth.mobile,
      }}
      sx={
        // If the image is on the right side, we want it to align to the
        // right (i.e. flex-end) if it doesn't fill the whole width of all
        // the columns it's been assigned:
        data.imageHorizontalAlignment === ImageHorizontalAlignment.Right
          ? {
            [muiTheme.breakpoints.not('mobile')]: {
              '& img': {
                float: 'right',
              },
            },
          }
          : {}
      }
    >
      <img src={imageUrl ?? ''} alt="" style={MODULE_IMAGE_BASE_STYLING} />
    </GridItem>
  ) : null;

  return (
    <GridContainer
      // https://mui.com/system/getting-started/the-sx-prop/#passing-the-sx-prop
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      sx={[MODULE_ROW_STYLE, ...(Array.isArray(sx) ? sx : [sx])]}
      alignItems="flex-start"
      columnSpacing={getSettingBasedOnBreakpoint(columnsSpacing)}
    >
      {data.headerCentered && data.header && (
        <GridItem
          size={{
            desktop: getAvailableColumnsCount(
              GRID_CONFIGS_FOR_BREAKPOINTS[BREAKPOINT.DESKTOP],
            ),
            tablet: getAvailableColumnsCount(
              GRID_CONFIGS_FOR_BREAKPOINTS[BREAKPOINT.TABLET],
            ),
            tabletMini: getAvailableColumnsCount(
              GRID_CONFIGS_FOR_BREAKPOINTS[BREAKPOINT.TABLET_MINI],
            ),
            mobile: getAvailableColumnsCount(
              GRID_CONFIGS_FOR_BREAKPOINTS[BREAKPOINT.MOBILE],
            ),
          }}
        >
          <Typography
            variant={headlineTypographyVariant}
            muiTheme={muiTheme}
            theme={theme}
            additionalStyles={centeredHeaderStyles}
          >
            <HtmlBox html={data.header} />
          </Typography>
        </GridItem>
      )}
      {data.showImage &&
        (data.imageHorizontalAlignment === ImageHorizontalAlignment.Left ||
          (data.imageHorizontalAlignment === ImageHorizontalAlignment.Right &&
            isMobile)) &&
        imageGridItem}
      <GridItem
        ref={contentGridItemRef}
        sx={NON_IMAGE_CONTENT_STYLE}
        size={{
          desktop:
            getAvailableColumnsCount(
              GRID_CONFIGS_FOR_BREAKPOINTS[BREAKPOINT.DESKTOP],
            ) - (data.showImage ? imageColumnWidth.desktop : 0),
          tablet:
            getAvailableColumnsCount(
              GRID_CONFIGS_FOR_BREAKPOINTS[BREAKPOINT.TABLET],
            ) - (data.showImage ? imageColumnWidth.tablet : 0),
          tabletMini:
            getAvailableColumnsCount(
              GRID_CONFIGS_FOR_BREAKPOINTS[BREAKPOINT.TABLET_MINI],
            ) - (data.showImage ? imageColumnWidth.tabletMini : 0),
          mobile:
            getAvailableColumnsCount(
              GRID_CONFIGS_FOR_BREAKPOINTS[BREAKPOINT.MOBILE],
            ) - (data.showImage ? imageColumnWidth.mobile : 0),
        }}
      >
        {!data.headerCentered && data.header && (
          <Typography
            variant={headlineTypographyVariant}
            additionalStyles={headerStyles}
            muiTheme={muiTheme}
            theme={theme}
          >
            <HtmlBox html={data.header} />
          </Typography>
        )}
        <Typography
          variant={contentTypographyVariant}
          muiTheme={muiTheme}
          theme={theme}
        >
          <HtmlBox
            html={
              isUserTerminated
                ? data.contentIfTerminatedUser ?? data.content
                : data.content
            }
          />
        </Typography>
        {(renderButtonElement || buttonData) && (
          <Box sx={{ marginTop: '40px' }}>
            {renderButtonElement &&
              renderButtonElement({
                label: buttonData?.text,
                dataElementId: button?.dataElementId,
                buttonVariant: button.buttonVariant ?? ButtonVariant.SECONDARY,
              })}
            {!renderButtonElement &&
              buttonData &&
              button &&
              isPageForButtonAvailable && (
                <LinkButton
                  to={buttonPageByStatus ? buttonRoute : buttonData.url ?? ''}
                  themeToken={themeToken}
                  variant={button.buttonVariant ?? ButtonVariant.SECONDARY}
                  dataElementId={button?.dataElementId}
                >
                  {buttonData.text}
                </LinkButton>
            )}
          </Box>
        )}
        {data.checkmarksList && !!data.checkmarksList.length && (
          <>
            {/* Divider line */}
            <Box
              sx={{
                borderTop: `1px solid ${theme.colors.grayscale.outlineGrey}`,
                height: '0px',
                width: '100%',
                margin: '32px 0',
              }}
            />
            <ul style={{ all: 'unset' }}>
              {data.checkmarksList.map((html) => (
                <CheckmarkBulletRow
                  // eslint-disable-next-line no-underscore-dangle
                  key={html.__html}
                  theme={theme}
                  muiTheme={muiTheme}
                >
                  <HtmlBox html={html} />
                </CheckmarkBulletRow>
              ))}
            </ul>
          </>
        )}
      </GridItem>
      {data.showImage &&
        data.imageHorizontalAlignment === ImageHorizontalAlignment.Right &&
        !isMobile &&
        imageGridItem}
    </GridContainer>
  );
};

export default ModuleWithImage;
